
import axios from "axios";
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
} from "./tokenManager";

const BASE_URL = process.env.REACT_APP_API_URL;

export const SESSION_EXPIRED_EVENT = "auth:session-expired";

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: false,
});

const authPost = (url, data) =>
  axios.post(url, data, {
    baseURL: BASE_URL,
    headers: {
      "Content-Type": "application/json",
    },
    withCredentials: false,
  });

const PUBLIC_AUTH_PATHS = [
  "/auth/request-otp/",
  "/auth/verify-otp/",
  "/auth/jwt/create/",
  "/auth/jwt/refresh/",
  "/auth/users/reset_password",
  "/auth/users/reset_password_confirm/",
];

const normalizePath = (url = "") => {
  const path = url.split("?")[0];
  return path.replace(/^\/api\/v\d+/, "");
};

const isPublicAuthRequest = (url = "") => {
  const path = normalizePath(url);
  return PUBLIC_AUTH_PATHS.some(
    (publicPath) => path.includes(publicPath),
  );
};

api.interceptors.request.use(
  (config) => {
    const access = getAccessToken();

    if (access) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${access}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      isPublicAuthRequest(originalRequest.url)
    ) {
      return Promise.reject(error);
    }

    const refresh = getRefreshToken();

    if (!refresh) {
      clearTokens();
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const response = await authPost("/auth/jwt/refresh/", {
        refresh,
      });

      const newAccess = response.data.access;
      const newRefresh = response.data.refresh || refresh;

      if (!newAccess) {
        throw new Error("Refresh response did not contain an access token.");
      }

      setTokens(newAccess, newRefresh);

      // The request interceptor attaches the newly stored access token.
      return api(originalRequest);
    } catch (refreshError) {
      clearTokens();
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      return Promise.reject(refreshError);
    }
  },
);

export const cookieApi = api;

export const logoutApi = async () => {
  const refresh = getRefreshToken();

  try {
    if (refresh) {
      await authPost("/auth/jwt/logout/", { refresh });
    }
  } catch {
    // Local logout still proceeds if server-side logout is unavailable.
  } finally {
    clearTokens();
  }
};
